/**
 * pages/me/myHazards.js — /me хуудасны "Аюулын бүртгэл" таб.
 * Нэвтэрсэн хэрэглэгчийн ӨӨРИЙН бүртгэсэн (insert_user_tn) аюулуудыг харуулна.
 *
 * Мөр бүрийн бүтэц:
 *   • Дугаар багана  — бүртгэлийн дугаар, ДООР нь аюулын төрөл (өнгө+icon+нэр)
 *                      ба төлөвийн badge.
 *   • Мэдээлэл багана — бүртгэсэн аюулын ТАЙЛБАР нь гол текст, доор нь бүтцийн
 *                      нэгж · байршил бүдэг өнгөөр.
 *   • Мөр дээр дарахад дэлгэрэнгүй ЦОНХ (modal) нээгдэнэ.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DateRangeFilter } from "src/components/filters";
import { useIsNarrow } from "src/components/ResponsiveTable";
import { useNavigate } from "react-router-dom";
import { Modal } from "antd";
import { useUserContext } from "src/contexts/userContext";
import { getHazards, getHazardById } from "src/api/hazard";
import { iconForCategory } from "src/components/hazard/common";
import { workColorOf } from "src/components/hazard/workGroup";
import { useHazardCategoryColors } from "src/components/risk/workTypeColors";
import { ThumbImg } from "src/utils/mediaThumb";

const GREEN = "#1B7A45";
const MUTED = "#A8AFBC";

/** Нэг хуудсанд харуулах мөрийн тоо. */
const PER_PAGE = 20;

/**
 * Бүртгэл ХААНААС ирсэн бэ — `hazard_reports.source` ("mobile" | "web").
 *
 * Гар утасны апп нь `source`-ыг илгээдэггүй тул баазын анхдагч "mobile"
 * болдог; вэб нь тодорхой "web" гэж илгээнэ (api/hazard.js).
 */
const SOURCE_META = {
  mobile: { label: "Гар утас", icon: "fa-mobile-alt", color: "#0EA5E9" },
  web: { label: "Вэб", icon: "fa-desktop", color: "#7C3AED" },
};
const sourceOf = (it) => SOURCE_META[it?.source] || SOURCE_META.mobile;

/**
 * Анхдагчаар харуулах хугацаа — ТУХАЙН САР.
 *
 * ЯАГААД: өмнө нь огнооны хязгааргүй 100 мөр татаад клиент тал дээр шүүдэг
 * байсан. Бүртгэл хуримтлагдах тусам /me-гийн энэ таб хүндэрдэг. Одоо
 * серверээс шууд хугацааны мужаар татна (`date_from`/`date_to`).
 *
 * Хэрэглэгч огнооны сонголтоор хугацааг ХЭЗЭЭ Ч өргөтгөж болно — тиймээс
 * хуучин бүртгэл "алга болохгүй", зөвхөн анхдагч харагдац л хязгаарлагдана.
 */

const dayStr = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

/** Анхны огнооны муж — ТУХАЙН САР.
 *  Өмнө нь сүүлийн 10 хоног байсан бөгөөд сарын эхэн үед хоосон харагдаж,
 *  хэрэглэгч огноогоо гараар өргөтгөх шаардлагатай болдог байв.
 *  Бусад хуудастай нэг зарчим (components/filters/currentMonthRange). */
function defaultRange() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return [dayStr(from), dayStr(to)];
}

/** АЮУЛЫН ГАРАЛ — `report_mode`-оор.
 *
 *  ⚠ Энэ хуудас өмнө нь `plain_only=1` илгээж, ЗӨВХӨН `report_mode` ХООСОН
 *    бүртгэлийг харуулдаг байв. Практикт ажилтан аюулаа ӨЭА, наряд, зөрчлийн
 *    урсгалаар бүртгэдэг болсон тул шинэ бүртгэл энд ОГТ ОРЖ ИРЭХГҮЙ болов:
 *    ТН 43917-ийн хувьд «энгийн» сүүлийн бүртгэл 2026-06-30-ных байсан атал
 *    ӨЭА-гийнх 2026-08-13-ных (2026-08-13-нд хэрэглэгч мэдэгдсэн).
 *
 *    Тиймээс одоо БҮХ гарлыг харуулж, гарлыг нь шошгоор ялгана. Хуучин
 *    зан үйл хэрэгтэй бол «Гарал» шүүлтээс «Аюул мэдээлэх»-ийг сонгоно.
 */
const ORIGINS = {
  "": { name: "Аюул мэдээлэх", color: "#16A34A" },
  naryad: { name: "Ажлын даалгавар", color: "#1677FF" },
  oea: { name: "ӨЭА", color: "#7C3AED" },
  stop_work: { name: "Ажил зогсоосон", color: "#B91C1C" },
  workplace_assessment: { name: "Ажлын байрны үнэлгээ", color: "#0EA5E9" },
  bn_violation: { name: "БН зөрчил", color: "#D97706" },
  hab_dept_violation: { name: "ХАБ хэлтсийн зөрчил", color: "#DB2777" },
  violation: { name: "Зөрчил", color: "#D97706" },
  inspection: { name: "Хяналт шалгалт", color: "#0891B2" },
};
/** Бүртгэлийн гарлын түлхүүр. `report_mode` хоосон = өөрийн санаачилгаар. */
const originKey = (it) => String(it?.report_mode || "");
const originOf = (it) =>
  ORIGINS[originKey(it)] || { name: it.report_mode, color: "#8A94A6" };

// Шүүлтийн хэсгийн нийтлэг оролтын хэв маяг
/**
 * Хэсгийн ЗУРААС — нэр нь хоёр шугамын ДУНД («—— Төлөв ——»).
 * Шүүлтийн бүлгүүдийг (төлөв / аюулын төрөл) нүдэнд ялгаж өгнө —
 * өмнө нь чипүүд нэг нэгнийхээ араас цувж, аль нь төлөв, аль нь төрөл
 * гэдэг нь харагддаггүй байв.
 */
function SectionDivider({ label }) {
  const line = { flex: 1, height: 1, background: "#E4E8EE" };
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        margin: "10px 0 6px",
      }}
    >
      <span style={line} />
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: 0.4,
          textTransform: "uppercase",
          color: "#8A94A6",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      <span style={line} />
    </div>
  );
}

const filterInput = {
  height: 32,
  padding: "0 10px",
  borderRadius: 8,
  border: "1px solid #DDE1E7",
  background: "#fff",
  fontSize: 12.5,
  color: "#1F2430",
  outline: "none",
};

const SEVERITY = {
  1: { name: "Бага", bg: "#E7F6EC", fg: "#16A34A" },
  2: { name: "Дунд", bg: "#FEF6E7", fg: "#CA8A04" },
  3: { name: "Өндөр", bg: "#FDEEE3", fg: "#EA580C" },
  // ⚠ ХУУЧИН 4/5 — сонголтод ГАРАХГҮЙ, зөвхөн баазад хоцорсон мөрийг
  //   «Өндөр» гэж зөв харуулахын тулд үлдээв (ХООСОН харагдах ёсгүй).
  4: { name: "Өндөр", bg: "#FCEBEB", fg: "#DC3545" },
  5: { name: "Өндөр", bg: "#FCEBEB", fg: "#DC3545" },
};

const STATUS = {
  NEW: { name: "Шинэ", bg: "#FDECEC", fg: "#DC2626" },
  TRIAGE: { name: "Хяналтад", bg: "#E4F0F8", fg: "#1F4E79" },
  ASSIGNED: { name: "Хуваарилсан", bg: "#E4F0F8", fg: "#1F4E79" },
  IN_PROGRESS: { name: "Хэрэгжиж буй", bg: "#FEF6E7", fg: "#CA8A04" },
  COMPLETED: { name: "Биелсэн", bg: "#E7F6EC", fg: "#16A34A" },
  REJECTED: { name: "Татгалзсан", bg: "#F1F2F4", fg: "#6B7280" },
};

function Badge({ map, value }) {
  const it = map[value] || { name: value, bg: "#F1F2F4", fg: "#6B7280" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        whiteSpace: "nowrap",
        background: it.bg,
        color: it.fg,
      }}
    >
      {it.name}
    </span>
  );
}

/** Аюулын төрөл — өнгөт icon + нэршил. */
function CategoryChip({ name, color }) {
  if (!name) return null;
  const ic = iconForCategory(name);
  const c = color || ic.color;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "1px 7px",
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 600,
        whiteSpace: "nowrap",
        background: c + "18",
        color: c,
        border: "1px solid " + c + "44",
      }}
    >
      <i className={"fas " + ic.icon} style={{ fontSize: 9 }} />
      {name}
    </span>
  );
}

/**
 * ActionBtn — мөрийн үйлдлийн icon товч.
 * pages/incident/register-ийн ижил нэртэй компоненттой НЭГ хэв маягтай —
 * бүх жагсаалт дээр үйлдлийн багана ижил харагдана.
 */
function ActionBtn({ icon, title, color, onClick }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      style={{
        width: 28,
        height: 28,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 7,
        border: `1px solid ${color}33`,
        background: color + "12",
        color,
        cursor: "pointer",
        fontSize: 12,
        padding: 0,
      }}
    >
      <i className={"fas " + icon} />
    </button>
  );
}

/** Бүртгэл ХААНААС ирснийг харуулах шошго — гар утас / вэб. */
function SourceBadge({ it }) {
  const m = sourceOf(it);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 10.5,
        fontWeight: 700,
        whiteSpace: "nowrap",
        background: m.color + "16",
        color: m.color,
        border: "1px solid " + m.color + "33",
      }}
    >
      <i className={"fas " + m.icon} style={{ fontSize: 10 }} />
      {m.label}
    </span>
  );
}

/** Бүртгэлийн ГАРАЛ — аль урсгалаас үүссэн бэ (`report_mode`). */
function OriginBadge({ it }) {
  const m = originOf(it);
  if (!m.name) return null;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 10.5,
        fontWeight: 700,
        whiteSpace: "nowrap",
        background: m.color + "16",
        color: m.color,
        border: "1px solid " + m.color + "33",
      }}
    >
      {m.name}
    </span>
  );
}

/** Хавсралтын жижиг товч — зураг (тоотой) эсвэл дуу хоолой. */
function MediaChip({ icon, color, count, title, onClick }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        height: 26,
        padding: "0 8px",
        borderRadius: 7,
        border: `1px solid ${color}33`,
        background: color + "12",
        color,
        cursor: "pointer",
        fontSize: 11.5,
        fontWeight: 700,
      }}
    >
      <i className={"fas " + icon} style={{ fontSize: 12 }} />
      {count != null ? count : null}
    </button>
  );
}

/**
 * Хуудаслалтын удирдлага.
 *
 * Мөр цөөн (нэг хуудсанд багтах) үед ОГТ харагдахгүй — хоосон зай эзэлнэ.
 */
function Pager({ page, totalPages, total, onChange }) {
  if (totalPages <= 1) return null;
  const btn = (disabled) => ({
    minWidth: 30,
    height: 28,
    borderRadius: 7,
    border: "1px solid #DDE1E7",
    background: disabled ? "#F7F8FA" : "#fff",
    color: disabled ? "#C4C9D1" : "#1F2430",
    cursor: disabled ? "default" : "pointer",
    fontSize: 12,
  });
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 6,
        padding: "10px 14px",
        borderTop: "1px solid #EDEFF3",
      }}
    >
      <span style={{ fontSize: 12, color: MUTED, marginRight: 6 }}>
        Нийт {total} · {page}/{totalPages} хуудас
      </span>
      <button
        type="button"
        style={btn(page <= 1)}
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        title="Өмнөх"
      >
        <i className="fas fa-chevron-left" />
      </button>
      <button
        type="button"
        style={btn(page >= totalPages)}
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
        title="Дараах"
      >
        <i className="fas fa-chevron-right" />
      </button>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("mn-MN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Дэлгэрэнгүй цонхны нэг талбар. */
function Field({ label, value, full }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div style={{ gridColumn: full ? "1 / -1" : "auto" }}>
      <div style={{ fontSize: 11, color: MUTED, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, color: "#1F2430" }}>{value}</div>
    </div>
  );
}

/**
 * Шийдвэрлэлтийн блок — ЗӨВХӨН ХАРАХ мэдээлэл.
 * Хариуцагч, хугацаа, ШИЙДВЭРЛЭЛТИЙН ТАЙЛАН (авсан арга хэмжээ),
 * ТАТГАЛЗСАН ШАЛТГААН зэргийг ажилтанд ойлгомжтой байдлаар харуулна.
 */
function ResolutionBlock({ detail }) {
  const st = detail.status;
  const done = st === "COMPLETED";
  const rejected = st === "REJECTED";
  const accent = done ? "#16A34A" : rejected ? "#DC2626" : "#CA8A04";
  const title = done
    ? "Шийдвэрлэлтийн тайлан"
    : rejected
    ? "Татгалзсан шалтгаан"
    : "Шийдвэрлэлтийн явц";

  // Хараахан шийдвэрлэгдээгүй, хариуцагч ч ороогүй бол блокийг харуулахгүй
  const hasAnything =
    done ||
    rejected ||
    detail.assigned_to_name ||
    detail.response_action ||
    detail.rejected_reason;
  if (!hasAnything) return null;

  return (
    <div
      style={{
        marginTop: 16,
        border: "1px solid #EDEFF3",
        borderLeft: "4px solid " + accent,
        borderRadius: 10,
        padding: 12,
        background: "#FCFDFE",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <i
          className={
            "fas " +
            (done
              ? "fa-check-circle"
              : rejected
              ? "fa-times-circle"
              : "fa-hourglass-half")
          }
          style={{ color: accent }}
        />
        <b style={{ color: accent, fontSize: 13 }}>{title}</b>
        <Badge map={STATUS} value={st} />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 12,
        }}
      >
        <Field label="Хариуцагч" value={detail.assigned_to_name} />
        <Field
          label="Хуваарилсан"
          value={detail.assigned_at ? fmtDate(detail.assigned_at) : null}
        />
        <Field
          label="Шийдвэрлэх хугацаа"
          value={detail.sla_deadline ? fmtDate(detail.sla_deadline) : null}
        />
        <Field
          label={rejected ? "Татгалзсан огноо" : "Биелсэн огноо"}
          value={
            rejected
              ? detail.rejected_at
                ? fmtDate(detail.rejected_at)
                : null
              : detail.completed_at
              ? fmtDate(detail.completed_at)
              : null
          }
        />
        <Field
          label={rejected ? "Татгалзсан" : "Биелүүлсэн"}
          value={rejected ? detail.rejected_by_name : detail.completed_by_name}
        />
      </div>

      {detail.response_action ? (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, color: MUTED, marginBottom: 3 }}>
            Авсан арга хэмжээ / тайлан
          </div>
          <div
            style={{
              background: "#F1FAF4",
              border: "1px solid #D8EEE1",
              borderRadius: 8,
              padding: 10,
              whiteSpace: "pre-wrap",
              lineHeight: 1.5,
              color: "#1F2430",
            }}
          >
            {detail.response_action}
          </div>
        </div>
      ) : null}

      {detail.rejected_reason ? (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, color: MUTED, marginBottom: 3 }}>
            Татгалзсан шалтгаан
          </div>
          <div
            style={{
              background: "#FDF2F2",
              border: "1px solid #F5D7D7",
              borderRadius: 8,
              padding: 10,
              whiteSpace: "pre-wrap",
              lineHeight: 1.5,
              color: "#1F2430",
            }}
          >
            {detail.rejected_reason}
          </div>
        </div>
      ) : null}

      {!done && !rejected && !detail.response_action ? (
        <div style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>
          Таны мэдээлэл хүлээн авагдсан. Шийдвэрлэлтийн тайлан гарсны дараа энд
          харагдана.
        </div>
      ) : null}
    </div>
  );
}

export default function MyHazards() {
  // Утсан дээр шүүлтийн талбар бүр БҮТЭН өргөн, өөрийн мөрөнд
  const narrow = useIsNarrow();
  const fw = narrow ? { flex: "1 1 100%", minWidth: "100%" } : null;
  const navigate = useNavigate();
  const { user } = useUserContext();
  const myTn = String(user?.info?.tn || user?.tn || "");
  const catColors = useHazardCategoryColors(); // лавлахын өнгө (byId / byName)
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Дэлгэрэнгүй цонх
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Зургийн ЖИЖИГ урьдчилан харах цонх — мөрийн зургийн icon дээр дарахад.
  // Дэлгэрэнгүй цонх нээхгүйгээр «энэ ямар зураг вэ» гэдгийг хурдан харна.
  const [photoPeek, setPhotoPeek] = useState(null); // { photos: [], title }

  /* Хугацааны цонхонд юу ч байхгүй үед «надад ер нь бүртгэл байна уу,
     хэзээ вэ?» гэдгийг ЗААЖ өгнө. Ингэснээр хэрэглэгч «систем эвдэрсэн»
     гэж бодохоо болино — огноогоо л өргөтгөх хэрэгтэй болохыг мэднэ.
     Зөвхөн хоосон гарсан үед НЭГ нэмэлт хүсэлт явна. */
  const [outside, setOutside] = useState(null); // { total, latest }

  // Эрэмбэ — хүснэгтийн толгой дээр дарж солино
  const [sortBy, setSortBy] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);

  // Хайлт / шүүлт
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fSeverity, setFSeverity] = useState("");
  const [fCategory, setFCategory] = useState("");
  // Гарал (report_mode). "" = бүх гарал, "plain" = зөвхөн «Аюул мэдээлэх».
  const [fOrigin, setFOrigin] = useState("");
  const [defaults] = useState(defaultRange);
  const [fFrom, setFFrom] = useState(defaults[0]);
  const [fTo, setFTo] = useState(defaults[1]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    // mine=1        → эрхээс үл хамааран ЗӨВХӨН өөрийн бүртгэсэн аюул.
    //                 (HSE ажилтанд бүх бүртгэл ирвэл өөрийнх нь эхний 100-д
    //                  багтахгүй унаж алга болдог байсан.)
    // ⚠ `plain_only=1` ХАСАВ. Тэр нь `report_mode` ХООСОН мөрийг л үлдээдэг
    //   байсан бөгөөд ажилтан аюулаа ӨЭА/наряд/зөрчлийн урсгалаар бүртгэдэг
    //   болсон тул шинэ бүртгэл энд ор тас орж ирэхгүй болов. Одоо БҮХ
    //   гарлыг татаж, «Гарал» шүүлт + мөр бүрийн шошгоор ялгана (ORIGINS).
    // date_from/to  → серверт шүүлгэнэ. Хугацаа өөрчлөгдөх бүрд ДАХИН татна.
    getHazards({
      per_page: 200,
      mine: 1,
      date_from: fFrom || undefined,
      date_to: fTo || undefined,
    })
      .then((res) => {
        if (!mounted) return;
        const rows = res.items || [];
        setItems(rows);
        // Хугацааны цонх хоосон бол — хугацаагүйгээр НЭГ мөр асууж, ажилтанд
        // ер нь бүртгэл байгаа эсэх ба сүүлийнх нь хэзээ болохыг тогтооно.
        if (rows.length === 0 && (fFrom || fTo)) {
          getHazards({ per_page: 1, mine: 1 })
            .then((all) => {
              if (!mounted) return;
              const first = (all.items || [])[0];
              setOutside({
                total: all.total || 0,
                latest: first?.report_date || null,
              });
            })
            .catch(() => mounted && setOutside(null));
        } else {
          setOutside(null);
        }
      })
      .catch(() => {
        if (mounted) setItems([]);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [fFrom, fTo]);

  // Дэлгэрэнгүйг цонх нээгдэх бүрд серверээс шинээр татна (зураг, түүхтэй)
  useEffect(() => {
    if (!detailId) return;
    let mounted = true;
    setDetailLoading(true);
    setDetail(null);
    getHazardById(detailId)
      .then((r) => mounted && setDetail(r))
      .catch(() => mounted && setDetail(null))
      .finally(() => mounted && setDetailLoading(false));
    return () => {
      mounted = false;
    };
  }, [detailId]);

  // Тухайн бүртгэлийн аюулын төрлүүд — hazard_category_ids (CSV) → лавлах
  const categoriesOf = useCallback(
    (it) => {
      const ids = String(it.hazard_category_ids || it.hazard_category_id || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      const list = ids
        .map((id) => {
          const ref = catColors.byId[id] || catColors.byId[Number(id)];
          return {
            id,
            name: ref?.name || (ids.length === 1 && it.hazard_category_name),
            color: ref?.color,
          };
        })
        .filter((c) => c.name);

      // Лавлахаас олдоогүй бол бүртгэлд хадгалсан нэрийг ашиглана
      if (list.length === 0 && it.hazard_category_name) {
        return [
          {
            id: it.hazard_category_id,
            name: it.hazard_category_name,
            color: catColors.byName[it.hazard_category_name],
          },
        ];
      }
      return list;
    },
    [catColors],
  );

  // Сервер аль хэдийн mine=1-ээр шүүсэн; энэ нь зөвхөн нэмэлт хамгаалалт
  // (хуучин backend дээр ажиллаж байгаа тохиолдолд).
  const mine = useMemo(
    () =>
      items
        .filter((i) => !myTn || String(i.insert_user_tn) === myTn)
        .sort((a, b) =>
          String(b.report_date || "").localeCompare(
            String(a.report_date || ""),
          ),
        ),
    [items, myTn],
  );

  // Тухайн бүртгэлийн ангиллын id-ууд (шүүлтэнд)
  const catIdsOf = useCallback(
    (it) =>
      String(it.hazard_category_ids || it.hazard_category_id || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    [],
  );

  // Төлөвөөс БУСАД бүх шүүлт — төлөвийн chip-ийн тоолуурыг үүн дээр бодно
  const baseFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return mine.filter((it) => {
      if (fSeverity && String(it.severity) !== fSeverity) return false;
      if (fCategory && !catIdsOf(it).includes(fCategory)) return false;
      // "plain" нь `report_mode` ХООСОН гэсэн үг — select-д хоосон утга нь
      // «бүх гарал»-д аль хэдийн эзэмшигдсэн тул тусдаа түлхүүр хэрэглэв.
      if (fOrigin && originKey(it) !== (fOrigin === "plain" ? "" : fOrigin))
        return false;

      const d = String(it.report_date || "").slice(0, 10);
      if (fFrom && d < fFrom) return false;
      if (fTo && d > fTo) return false;

      if (q) {
        const hay = [
          it.report_number,
          it.description,
          it.voice_transcript,
          it.location_text,
          it.affected_location,
          it.insert_dept_name,
          it.hazard_category_name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [mine, search, fSeverity, fCategory, fOrigin, fFrom, fTo, catIdsOf]);

  /* Аюулын ТӨРЛИЙН тоолуур — чипэн дээр харуулна.
     Нэг бүртгэлд ОЛОН төрөл сонгогдож болдог (hazard_category_ids нь CSV)
     тул мөрийг төрөл бүрд нь тоолно. */
  const categoryCounts = useMemo(() => {
    const c = {};
    baseFiltered.forEach((it) => {
      catIdsOf(it).forEach((id) => {
        c[id] = (c[id] || 0) + 1;
      });
    });
    return c;
  }, [baseFiltered, catIdsOf]);

  const statusCounts = useMemo(() => {
    const c = {};
    baseFiltered.forEach((it) => {
      c[it.status] = (c[it.status] || 0) + 1;
    });
    return c;
  }, [baseFiltered]);

  const filtered = useMemo(
    () => (fStatus ? baseFiltered.filter((it) => it.status === fStatus) : baseFiltered),
    [baseFiltered, fStatus],
  );

  /* ── ЭРЭМБЭ ───────────────────────────────────────────────────────────
     Хүснэгтийн толгой дээр дарж эрэмбэлнэ. Дахин дарахад чиглэл эргэнэ.

     ⚠ Ажлаар бүлэглэх (`groupRowsByWork`) логикийг ХАСАВ: энэ таб одоо
     зөвхөн «Аюул мэдээлэх» бүртгэлийг харуулдаг бөгөөд тэдгээрийн 100%
     нь ажилтай холбоогүй (naryad_job_id / oea_permit_id хоосон). Тиймээс
     «Ажлын дугаар / Ажлын нэр» багана мөнхөд хоосон байх байсан. */
  const sortValue = useCallback((it, key) => {
    switch (key) {
      case "number":
        return String(it.report_number || "");
      case "status":
        return String(it.status || "");
      case "severity":
        return Number(it.severity ?? 0);
      case "source":
        return String(it.source || "mobile");
      case "media":
        // Хавсралттай нь эхэнд — зураг 2 оноо, дуу хоолой 1
        return (it.photos?.length ? 2 : 0) + (it.voice_file_url ? 1 : 0);
      case "date":
      default:
        return String(it.report_date || "");
    }
  }, []);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const va = sortValue(a, sortBy);
      const vb = sortValue(b, sortBy);
      if (va === vb) {
        // Тогтвортой хоёрдогч эрэмбэ — ижил утгатай мөр байрандаа үсэрхгүй
        return String(b.report_date || "").localeCompare(String(a.report_date || ""));
      }
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
    return arr;
  }, [filtered, sortBy, sortDir, sortValue]);

  /* ── ХУУДАСЛАЛТ ───────────────────────────────────────────────────── */
  const totalPages = Math.max(1, Math.ceil(sorted.length / PER_PAGE));
  // Шүүлт өөрчлөгдөхөд одоогийн хуудас байхгүй болж хоосон харагдахаас сэргийлнэ
  const safePage = Math.min(page, totalPages);
  const paged = useMemo(
    () => sorted.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE),
    [sorted, safePage],
  );

  // Шүүлт/эрэмбэ өөрчлөгдөх бүрд 1-р хуудас руу
  useEffect(() => {
    setPage(1);
  }, [search, fSeverity, fCategory, fOrigin, fStatus, fFrom, fTo, sortBy, sortDir]);

  // Шүүлтийн ангиллын сонголт — лавлах байхгүй бол бүртгэлээс гаргана
  const categoryOptions = useMemo(() => {
    if (catColors.list && catColors.list.length > 0) {
      return catColors.list.map((c) => ({ id: String(c.id), name: c.name }));
    }
    const seen = new Map();
    mine.forEach((it) => {
      if (it.hazard_category_id && it.hazard_category_name) {
        seen.set(String(it.hazard_category_id), it.hazard_category_name);
      }
    });
    return [...seen].map(([id, name]) => ({ id, name }));
  }, [catColors, mine]);

  // Огнооны АНХДАГЧ муж нь "шүүлт" гэж тооцогдохгүй — эс бөгөөс «Цэвэрлэх»
  // товч байнга харагдана.
  const dateChanged = fFrom !== defaults[0] || fTo !== defaults[1];
  const hasFilter =
    !!search || !!fStatus || !!fSeverity || !!fCategory || !!fOrigin || dateChanged;

  const clearFilters = () => {
    setSearch("");
    setFStatus("");
    setFSeverity("");
    setFCategory("");
    setFOrigin("");
    // Огноог ХООСОН биш, анхдагч 10 хоног руу буцаана — эс бөгөөс «Цэвэрлэх»
    // дарахад бүх түүхийг татаж эхэлнэ.
    setFFrom(defaults[0]);
    setFTo(defaults[1]);
  };

  // ⚠ Ачаалж байх/хоосон үед ЭРТ БУЦААХГҮЙ. Анхдагчаар зөвхөн 10 хоног
  // харуулдаг болсон тул хэрэглэгч хоосон хариу авахдаа огнооны сонголтыг
  // ЗААВАЛ харах ёстой — эс бөгөөс хугацаагаа өргөтгөх арга байхгүй болно.
  const detailCats = detail ? categoriesOf(detail) : [];

  return (
    <div>
      {/* ── Хайлт + шүүлтийн хэсэг ───────────────────────────────────── */}
      <div
        style={{
          border: "1px solid #EDEFF3",
          borderRadius: 10,
          padding: 10,
          marginBottom: 10,
          background: "#FCFDFE",
        }}
      >
        {/* 1-р мөр — хайлт, түвшин, төрөл, огнооны муж, цэвэрлэх */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div style={{ position: "relative", flex: "1 1 240px", minWidth: 200, ...(fw || {}) }}>
            <i
              className="fas fa-search"
              style={{
                position: "absolute",
                left: 10,
                top: 9,
                fontSize: 12,
                color: MUTED,
              }}
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Дугаар, тайлбар, байршил, нэгжээр хайх..."
              style={{ ...filterInput, width: "100%", paddingLeft: 30 }}
            />
            {search ? (
              <i
                className="fas fa-times-circle"
                title="Цэвэрлэх"
                onClick={() => setSearch("")}
                style={{
                  position: "absolute",
                  right: 10,
                  top: 9,
                  fontSize: 12,
                  color: MUTED,
                  cursor: "pointer",
                }}
              />
            ) : null}
          </div>

          <select
            value={fSeverity}
            onChange={(e) => setFSeverity(e.target.value)}
            style={{ ...filterInput, minWidth: 130, ...(fw || {}) }}
          >
            <option value="">Бүх түвшин</option>
            {Object.entries(SEVERITY).map(([k, v]) => (
              <option key={k} value={k}>
                {v.name}
              </option>
            ))}
          </select>

          {/* ГАРАЛ — аюул аль урсгалаас бүртгэгдсэн бэ. Анхдагчаар БҮГД
              (өмнөх `plain_only` зан үйл нь «Аюул мэдээлэх» сонголт болов). */}
          <select
            value={fOrigin}
            onChange={(e) => setFOrigin(e.target.value)}
            style={{ ...filterInput, minWidth: 150, ...(fw || {}) }}
          >
            <option value="">Бүх гарал</option>
            <option value="plain">{ORIGINS[""].name}</option>
            {Object.entries(ORIGINS)
              .filter(([k]) => k)
              .map(([k, v]) => (
                <option key={k} value={k}>
                  {v.name}
                </option>
              ))}
          </select>

          {/* Native `type="date"` нь браузераас хамаарч `mm/dd/yyyy` гэсэн
              англи зөвлөмж харуулдаг тул antd RangePicker-ээр сольсон */}
          <DateRangeFilter
            basis={narrow ? "100%" : 260}
            from={fFrom}
            to={fTo}
            onFromChange={setFFrom}
            onToChange={setFTo}
          />

          {hasFilter ? (
            // ICON товч — текст авсны дараа `filterInput`-ийн өргөн (input-д
            // зориулсан) илүүдэж, дан icon дунд нь эргэлдэнэ. Тогтмол дөрвөлжин.
            <button
              type="button"
              onClick={clearFilters}
              title="Цэвэрлэх"
              aria-label="Цэвэрлэх"
              style={{
                ...filterInput,
                width: 38,
                minWidth: 38,
                flex: "0 0 auto",
                padding: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "#DC2626",
                borderColor: "#F5D7D7",
                background: "#FDF2F2",
              }}
            >
              <i className="fas fa-eraser" />
            </button>
          ) : null}
        </div>

        {/* 2-р мөр — төлөвийн шүүлт (тоолууртай) */}
        <SectionDivider label="Төлөв" />
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 6,
          }}
        >
          {[["", "Бүгд"], ...Object.entries(STATUS).map(([k, v]) => [k, v.name])].map(
            ([key, label]) => {
              const active = fStatus === key;
              const count =
                key === "" ? baseFiltered.length : statusCounts[key] || 0;
              const c = key === "" ? GREEN : STATUS[key].fg;
              return (
                <button
                  key={key || "ALL"}
                  type="button"
                  onClick={() => setFStatus(key)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "3px 10px",
                    borderRadius: 999,
                    fontSize: 11.5,
                    fontWeight: 700,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    background: active ? c : "#fff",
                    color: active ? "#fff" : c,
                    border: "1px solid " + (active ? c : c + "44"),
                    opacity: !active && count === 0 ? 0.45 : 1,
                  }}
                >
                  {label}
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      padding: "0 5px",
                      borderRadius: 999,
                      background: active ? "rgba(255,255,255,0.28)" : c + "18",
                    }}
                  >
                    {count}
                  </span>
                </button>
              );
            },
          )}

          <span style={{ marginLeft: "auto", fontSize: 12, color: MUTED }}>
            Нийт {filtered.length} / {mine.length}
          </span>
        </div>

        {/* ── АЮУЛЫН ТӨРӨЛ — чипээр ИЛ ──────────────────────────────────
            Өмнө нь «Бүх төрөл» гэсэн dropdown дотор нуугдаж байсан тул
            хэрэглэгч ямар төрлүүд байгаа, аль нь хэдэн бүртгэлтэйг
            нээж үзэхээс нааш мэдэхгүй байв. Төлөвийн чиптэй ИЖИЛ
            загвараар ил гаргав — лавлахын өнгө/icon-той. */}
        {categoryOptions.length ? (
          <>
          <SectionDivider label="Аюулын төрөл" />
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 6,
            }}
          >
            {[{ id: "", name: "Бүгд" }, ...categoryOptions].map((c) => {
              const active = fCategory === String(c.id);
              const count =
                c.id === "" ? baseFiltered.length : categoryCounts[String(c.id)] || 0;
              const ic = iconForCategory(c.name);
              // ⚠ `catColors.byId[id]` нь ӨНГӨ биш, {name,color,icon} ОБЪЕКТ
              //   буцаадаг (workTypeColors.js). Шууд style-д тавивал буруу
              //   CSS утга болно — `.color`-ыг задалж авна.
              const ref =
                catColors.byId[c.id] || catColors.byId[Number(c.id)] || null;
              const col =
                c.id === ""
                  ? GREEN
                  : ref?.color || catColors.byName[c.name] || ic.color;
              return (
                <button
                  key={c.id || "ALL"}
                  type="button"
                  onClick={() => setFCategory(String(c.id))}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "3px 10px",
                    borderRadius: 999,
                    fontSize: 11.5,
                    fontWeight: 700,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    background: active ? col : "#fff",
                    color: active ? "#fff" : col,
                    border: "1px solid " + (active ? col : col + "44"),
                    opacity: !active && count === 0 ? 0.45 : 1,
                  }}
                >
                  {c.id === "" ? null : (
                    <i className={"fas " + ic.icon} style={{ fontSize: 10 }} />
                  )}
                  {c.name}
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      padding: "0 5px",
                      borderRadius: 999,
                      background: active ? "rgba(255,255,255,0.28)" : col + "18",
                    }}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          </>
        ) : null}
      </div>

      <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "#F7F8FA" }}>
            {[
              { label: "№", key: null },
              { label: "Дугаар", key: "number" },
              { label: "Аюулын мэдээлэл", key: null },
              { label: "Эх сурвалж / Гарал", key: "source" },
              { label: "Хавсралт", key: "media" },
              { label: "Төлөв · Огноо", key: "date" },
              { label: "Түвшин", key: "severity" },
              { label: "Үйлдэл", key: null },
            ].map((h) => {
              const active = h.key && sortBy === h.key;
              return (
                <th
                  key={h.label}
                  onClick={
                    h.key
                      ? () => {
                          // Ижил баганыг дахин дарвал чиглэл эргэнэ
                          if (sortBy === h.key) {
                            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                          } else {
                            setSortBy(h.key);
                            setSortDir("desc");
                          }
                        }
                      : undefined
                  }
                  title={h.key ? "Эрэмбэлэх" : undefined}
                  style={{
                    textAlign: "left",
                    padding: "10px 14px",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    color: active ? GREEN : "#8A94A6",
                    cursor: h.key ? "pointer" : "default",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  {h.label}
                  {h.key ? (
                    <i
                      className={
                        "fas " +
                        (active
                          ? sortDir === "asc"
                            ? "fa-sort-up"
                            : "fa-sort-down"
                          : "fa-sort")
                      }
                      style={{
                        marginLeft: 5,
                        opacity: active ? 1 : 0.35,
                        fontSize: 10,
                      }}
                    />
                  ) : null}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td
                colSpan={8}
                style={{
                  padding: "30px 0",
                  textAlign: "center",
                  color: MUTED,
                  fontSize: 13,
                }}
              >
                <i className="fas fa-spinner fa-spin" /> Уншиж байна...
              </td>
            </tr>
          ) : filtered.length === 0 ? (
            <tr>
              <td
                colSpan={8}
                style={{
                  padding: "30px 0",
                  textAlign: "center",
                  color: MUTED,
                  fontSize: 13,
                }}
              >
                {hasFilter
                  ? "Хайлт/шүүлтэнд тохирох бүртгэл олдсонгүй."
                  : "Сонгосон хугацаанд таны мэдээлсэн аюул алга байна."}
                {outside && outside.total > 0 ? (
                  <div style={{ marginTop: 8, color: "#5A6478" }}>
                    Танд нийт <b>{outside.total}</b> бүртгэл байна
                    {outside.latest
                      ? ` — сүүлийнх нь ${String(outside.latest).slice(0, 10)}`
                      : ""}
                    .
                  </div>
                ) : null}
                {/* ── НЭГ ДАРААД БҮХ ХУГАЦААГ ХАРАХ ──────────────────────
                    Таб нь анхдагчаар ТУХАЙН САРЫГ харуулдаг. Тухайн сард
                    бүртгэл байхгүй бол хэрэглэгч «жагсаалт огт ирэхгүй
                    байна» гэж ойлгодог — огнооны мужаа гараар өргөтгөнө
                    гэдгийг бага хүн анзаардаг. Тиймээс шууд товч тавив. */}
                <div style={{ marginTop: 12 }}>
                  <button
                    type="button"
                    onClick={() => {
                      // Хугацааны хязгаарыг БҮРЭН авна — сервер бүх
                      // бүртгэлийг буцаана (date_from/date_to явуулахгүй).
                      setFFrom("");
                      setFTo("");
                    }}
                    style={{
                      height: 32,
                      padding: "0 14px",
                      borderRadius: 8,
                      border: `1px solid ${GREEN}44`,
                      background: GREEN + "12",
                      color: GREEN,
                      fontSize: 12.5,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {/* FA5 нэр — төсөлд Font Awesome FREE 5.11.2 суусан тул
                        FA6-ийн `fa-history` ХАРАГДАХГҮЙ. */}
                    <i className="fas fa-history" style={{ marginRight: 6 }} />
                    Бүх хугацаагаар харах
                  </button>
                </div>
              </td>
            </tr>
          ) : null}
          {!loading &&
            paged.map((it, idx) => (
            <tr
              key={it.id}
              style={{ borderTop: "1px solid #EDEFF3", cursor: "pointer" }}
              onClick={() => setDetailId(it.id)}
            >
              <td
                style={{
                  padding: "10px 14px",
                  color: "#8A94A6",
                  fontSize: 13,
                  textAlign: "center",
                  width: 44,
                  verticalAlign: "top",
                }}
              >
                {(safePage - 1) * PER_PAGE + idx + 1}
              </td>


              {/* Дугаар + доор нь аюулын төрөл.
                  Дугаар нь туслах мэдээлэл тул жижиг, monospace —
                  тайлбар (гол агуулга) нүдэнд илүү тод үлдэнэ. */}
              <td
                style={{
                  padding: "10px 12px",
                  verticalAlign: "top",
                  width: 150,
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    color: GREEN,
                    fontSize: 11,
                    fontFamily: "monospace",
                    letterSpacing: 0.2,
                    whiteSpace: "nowrap",
                  }}
                >
                  {it.report_number}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 3,
                    marginTop: 3,
                  }}
                >
                  {categoriesOf(it).map((c) => (
                    <CategoryChip key={c.id} name={c.name} color={c.color} />
                  ))}
                </div>
              </td>

              {/* Аюулын мэдээлэл — тайлбар нь гол, БН нэр/байршил бүдэг */}
              <td style={{ padding: "10px 14px", verticalAlign: "top" }}>
                <div
                  style={{
                    fontWeight: 600,
                    color: "#1F2430",
                    fontSize: 13,
                    maxWidth: 460,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {it.description || it.voice_transcript || "—"}
                </div>

                {/* Аль асуумж дээр илэрсэн — ажлын нэр/дугаар нь тусдаа
                    багана болсон тул энд зөвхөн чеклистийн заалт үлдэнэ. */}
                {it.prework_checklist_label && it.prework_item_no != null ? (
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      color: workColorOf(it).fg,
                      marginTop: 3,
                    }}
                  >
                    {it.prework_checklist_label} #{it.prework_item_no}
                    {it.prework_item_title ? ` · ${it.prework_item_title}` : ""}
                  </div>
                ) : null}

                <div
                  style={{
                    fontSize: 12,
                    color: MUTED,
                    maxWidth: 460,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    marginTop: 2,
                  }}
                >
                  {[
                    it.insert_dept_name,
                    it.location_text || it.affected_location,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </div>
              </td>

              {/* ── ЭХ СУРВАЛЖ — гар утаснаас уу, вэбээс үү + ГАРАЛ ────
                  Гарал (ӨЭА / наряд / зөрчил …) нь энэ жагсаалтад БҮХ
                  урсгалын бүртгэл орж ирдэг болсны дараа заавал хэрэгтэй —
                  эс бөгөөс аль нь өөрийн санаачилгын мэдээлэл, аль нь
                  чек листээс үүссэн болох нь ялгагдахгүй. */}
              <td
                style={{
                  padding: "10px 14px",
                  verticalAlign: "top",
                  whiteSpace: "nowrap",
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <SourceBadge it={it} />
                  <OriginBadge it={it} />
                </div>
              </td>

              {/* ── ХАВСРАЛТ — зураг ба дуу хоолой ─────────────────────
                  Зургийн icon дээр дарахад дэлгэрэнгүй цонх нээхгүйгээр
                  ЖИЖИГ урьдчилан харах цонх гарна. */}
              <td
                style={{
                  padding: "10px 14px",
                  verticalAlign: "top",
                  whiteSpace: "nowrap",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {it.photos?.length ? (
                    <MediaChip
                      icon="fa-image"
                      color="#0EA5E9"
                      count={it.photos.length}
                      title={`${it.photos.length} зураг — харах`}
                      onClick={() =>
                        setPhotoPeek({
                          photos: it.photos,
                          title: it.report_number,
                        })
                      }
                    />
                  ) : null}
                  {it.voice_file_url ? (
                    <MediaChip
                      icon="fa-microphone"
                      color={GREEN}
                      title="Дуу хоолой сонсох"
                      onClick={() => {
                        new Audio(it.voice_file_url).play().catch(() => {});
                      }}
                    />
                  ) : null}
                  {!it.photos?.length && !it.voice_file_url ? (
                    <span style={{ color: "#D5DAE2", fontSize: 12 }}>—</span>
                  ) : null}
                </div>
              </td>

              {/* Төлөв — бүртгэсэн огноотой хамт, тусдаа багана */}
              <td
                style={{
                  padding: "10px 14px",
                  verticalAlign: "top",
                  whiteSpace: "nowrap",
                }}
              >
                <Badge map={STATUS} value={it.status} />
                <div
                  style={{ fontSize: 11.5, color: "#5A6478", marginTop: 4 }}
                >
                  {fmtDate(it.report_date)}
                </div>
              </td>

              <td style={{ padding: "10px 14px", verticalAlign: "top" }}>
                <Badge map={SEVERITY} value={it.severity} />
              </td>

              {/* ── ҮЙЛДЭЛ ────────────────────────────────────────────
                  Мөр дарахад дэлгэрэнгүй цонх нээгддэг ч ил товч байх нь
                  ойлгомжтой. «Засах» нь зөвхөн мэдээлснээс хойш 2 минутын
                  дотор, NEW төлөвт (backend `is_editable`/`editable_until`). */}
              <td
                style={{ padding: "10px 14px", verticalAlign: "top" }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{ display: "flex", gap: 4, justifyContent: "center" }}
                >
                  <ActionBtn
                    icon="fa-eye"
                    title="Харах"
                    color="#0EA5E9"
                    onClick={() => setDetailId(it.id)}
                  />
                  {it.voice_file_url ? (
                    <ActionBtn
                      icon="fa-microphone"
                      title="Дуу хоолой сонсох"
                      color={GREEN}
                      onClick={() => {
                        new Audio(it.voice_file_url).play().catch(() => {});
                      }}
                    />
                  ) : null}
                  {it.prework_item_type ? (
                    <ActionBtn
                      icon="fa-clipboard-check"
                      title="Холбогдох чек лист"
                      color="#1B4C8F"
                      onClick={() =>
                        navigate(
                          it.prework_item_type === "CRITICAL"
                            ? "/highrisk/prework15"
                            : "/highrisk/prework5a",
                        )
                      }
                    />
                  ) : null}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pager
        page={safePage}
        totalPages={totalPages}
        total={sorted.length}
        onChange={setPage}
      />
      </div>

      {/* ── Зургийн ЖИЖИГ урьдчилан харах цонх ─────────────────────────
          Дэлгэрэнгүй цонх нээлгүйгээр зургийг хурдан шалгах зориулалттай. */}
      <Modal
        open={!!photoPeek}
        onCancel={() => setPhotoPeek(null)}
        footer={null}
        width={520}
        title={
          <span style={{ fontSize: 13 }}>
            <i className="fas fa-image" style={{ marginRight: 8, color: "#0EA5E9" }} />
            {photoPeek?.title} · {photoPeek?.photos?.length || 0} зураг
          </span>
        }
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          {(photoPeek?.photos || []).map((ph) => (
            <a
              key={ph.id || ph.file_url}
              href={ph.file_url}
              target="_blank"
              rel="noreferrer"
              title="Бүтэн хэмжээгээр нээх"
            >
              {/* ⚠ 150×150-аар харуулж БҮТЭН файлыг татдаг байв
                  (2026-10-05). Холбоос нь бүтэн зургийг шинэ таб-д
                  нээдэг тул жижиг хувилбар хангалттай. */}
              <ThumbImg
                full={ph.file_url}
                thumb={ph.thumb_url}
                alt=""
                style={{
                  width: 150,
                  height: 150,
                  objectFit: "cover",
                  borderRadius: 8,
                  border: "1px solid #EDEFF3",
                }}
              />
            </a>
          ))}
        </div>
      </Modal>

      {/* ── Дэлгэрэнгүй цонх ─────────────────────────────────────────── */}
      <Modal
        open={!!detailId}
        onCancel={() => setDetailId(null)}
        width={760}
        footer={
          <div
            style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
          >
            <button
              type="button"
              onClick={() => setDetailId(null)}
              style={{
                padding: "6px 16px",
                borderRadius: 8,
                border: "1px solid #DDE1E7",
                background: "#fff",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Хаах
            </button>
            <button
              type="button"
              onClick={() => navigate("/hazard/" + detailId)}
              style={{
                padding: "6px 16px",
                borderRadius: 8,
                border: "none",
                background: GREEN,
                color: "#fff",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Дэлгэрэнгүй хуудас <i className="fas fa-chevron-right" />
            </button>
          </div>
        }
        title={
          <div style={{ paddingRight: 24 }}>
            <span style={{ fontWeight: 800, color: GREEN, fontSize: 15 }}>
              {detail?.report_number || "Аюулын бүртгэл"}
            </span>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                marginTop: 6,
              }}
            >
              {detailCats.map((c) => (
                <CategoryChip key={c.id} name={c.name} color={c.color} />
              ))}
              {detail && <Badge map={STATUS} value={detail.status} />}
              {detail && <Badge map={SEVERITY} value={detail.severity} />}
            </div>
          </div>
        }
      >
        {detailLoading ? (
          <div style={{ padding: "30px 0", textAlign: "center", color: MUTED }}>
            <i className="fas fa-spinner fa-spin" /> Уншиж байна...
          </div>
        ) : !detail ? (
          <div style={{ padding: "30px 0", textAlign: "center", color: MUTED }}>
            Мэдээлэл татаж чадсангүй.
          </div>
        ) : (
          <div style={{ fontSize: 13 }}>
            {/* Гол мэдээлэл — тайлбар */}
            <div
              style={{
                background: "#F7F8FA",
                borderRadius: 10,
                padding: 12,
                color: "#1F2430",
                whiteSpace: "pre-wrap",
                lineHeight: 1.5,
              }}
            >
              {detail.description || detail.voice_transcript || "—"}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 12,
                marginTop: 14,
              }}
            >
              <Field label="Бүтцийн нэгж" value={detail.insert_dept_name} />
              <Field label="Байршил" value={detail.location_text} />
              <Field
                label="Өртөж болзошгүй байршил"
                value={detail.affected_location}
              />
              <Field
                label="Өртөх хүний тоо"
                value={detail.affected_people_count}
              />
              <Field
                label="Өөрөө аюулд өртсөн эсэх"
                value={detail.self_in_danger ? "Тийм" : null}
              />
              <Field label="Бүртгэсэн" value={detail.insert_user_name} />
              <Field label="Огноо" value={fmtDate(detail.report_date)} />
            </div>

            {/* Шийдвэрлэлт — зөвхөн харах мэдээлэл */}
            <ResolutionBlock detail={detail} />

            {detail.photos && detail.photos.length > 0 ? (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 11, color: MUTED, marginBottom: 6 }}>
                  Зураг ({detail.photos.length})
                </div>
                <div
                  style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
                >
                  {detail.photos.map((p, i) => (
                    <a
                      key={p.id ?? i}
                      href={p.file_url}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        width: 96,
                        height: 96,
                        borderRadius: 8,
                        overflow: "hidden",
                        border: "1px solid #EDEFF3",
                        display: "block",
                      }}
                    >
                      {/* Дэлгэрэнгүйн зургийн тор — жижиг хувилбар
                          (бүтнийг `<a>` шинэ таб-д нээнэ) */}
                      <ThumbImg
                        full={p.file_url}
                        thumb={p.thumb_url}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </a>
                  ))}
                </div>
              </div>
            ) : null}

            {detail.history && detail.history.length > 0 ? (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 11, color: MUTED, marginBottom: 6 }}>
                  Шийдвэрлэлтийн явц
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {detail.history.map((h, i) => (
                    <div
                      key={h.id ?? i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                        borderLeft: "2px solid #EDEFF3",
                        paddingLeft: 10,
                      }}
                    >
                      <Badge map={STATUS} value={h.to_status} />
                      <span style={{ fontSize: 12, color: "#5A6478" }}>
                        {h.action_user_name || "-"} · {fmtDate(h.action_date)}
                      </span>
                      {h.note ? (
                        <span style={{ fontSize: 12, color: MUTED }}>
                          {h.note}
                        </span>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </Modal>
    </div>
  );
}
